import './LoginPage.css';

import React, { useState } from 'react';

import {
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { usePostHog } from '@posthog/react';

import Button from '../components/common/Button';
import Input from '../components/common/Input';
import PomPom from '../components/common/PomPom/PomPom';
import { useAuth } from '../contexts/AuthContext';

type DebugPreview = 'none' | 'success' | 'error';

const isDevBuild = process.env.NODE_ENV === 'development';

const logoSrc = `${process.env.PUBLIC_URL}/logo.svg`;

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [debugPreview, setDebugPreview] = useState<DebugPreview>('none');
  const { currentUser, login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const displayError =
    debugPreview === 'error'
      ? 'Failed to sign in'
      : debugPreview === 'success'
        ? ''
        : error;

  const displayMessage =
    debugPreview === 'success'
      ? 'Password reset email sent'
      : debugPreview === 'error'
        ? ''
        : message;

  const isOwner = currentUser !== null && currentUser.email !== null;

  if (isOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      posthog?.identify(email);
      posthog?.capture('admin_signed_in');
      navigate('/dashboard');
    } catch (submitErr: unknown) {
      setError('Failed to sign in');
      posthog?.captureException(submitErr);
      console.error(submitErr);
    }

    setLoading(false);
  }

  async function handleForgotPassword(): Promise<void> {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setMessage('');
      setError('');
      setLoading(true);
      await resetPassword(email);
      posthog?.capture('password_reset_requested');
      setMessage('Password reset email sent');
    } catch (resetErr: unknown) {
      setError('Failed to reset password');
      posthog?.captureException(resetErr);
      console.error(resetErr);
    }

    setLoading(false);
  }

  const handleDebugSuccess = (): void => {
    setDebugPreview((p) => (p === 'success' ? 'none' : 'success'));
  };

  const handleDebugError = (): void => {
    setDebugPreview((p) => (p === 'error' ? 'none' : 'error'));
  };

  const debugControls = isDevBuild ? (
    <div className="login-page-debug">
      <Button
        type="button"
        variant="secondary"
        compact
        onClick={handleDebugSuccess}
      >
        {debugPreview === 'success' ? 'Hide' : 'Show'} success banner (debug)
      </Button>
      <Button
        type="button"
        variant="secondary"
        compact
        onClick={handleDebugError}
      >
        {debugPreview === 'error' ? 'Hide' : 'Show'} error banner (debug)
      </Button>
    </div>
  ) : null;

  return (
    <div className="login-page">
      <div className="login-gutter-pompoms" aria-hidden="true">
        <PomPom size="xlarge" color="pink" className="login-gutter-pompom login-gutter-pompom-right-top" />
        <PomPom size="large" color="teal" className="login-gutter-pompom login-gutter-pompom-left-mid" />
        <PomPom size="xlarge" color="teal" className="login-gutter-pompom login-gutter-pompom-right-bottom" />
        <PomPom size="medium" color="pink" className="login-gutter-pompom login-gutter-pompom-left-bottom" />
        <PomPom size="large" color="pink" className="login-gutter-pompom login-gutter-pompom-right-edge-top" />
        <PomPom size="large" color="teal" className="login-gutter-pompom login-gutter-pompom-left-edge-top" />
        <PomPom size="xlarge" color="teal" className="login-gutter-pompom login-gutter-pompom-right-edge-bottom" />
        <PomPom size="medium" color="pink" className="login-gutter-pompom login-gutter-pompom-left-edge-bottom" />
      </div>

      <div className="login-hero">
        <PomPom size="medium" color="pink" className="login-hero-pompom login-hero-pompom-tr" />
        <PomPom size="small" color="teal" className="login-hero-pompom login-hero-pompom-bl" />
        <div className="login-hero-header-row">
          <h1 className="login-hero-title">
            Admin<br />
            <span className="login-hero-accent">login</span>
          </h1>
          <img src={logoSrc} alt="CheerEval" className="login-logo" />
        </div>
        <p className="login-hero-sub">Sign in to manage your tryouts.</p>
      </div>

      <div className="login-card">
        {displayError ? (
          <div className="login-page-error" role="alert">{displayError}</div>
        ) : null}

        {displayMessage ? (
          <div className="login-page-success" role="status">{displayMessage}</div>
        ) : null}

        <form className="login-page-form" onSubmit={handleSubmit}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            label="Email"
            required
            autoComplete="email"
          />

          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label="Password"
            required
            autoComplete="current-password"
          />

          <button type="submit" className="login-btn-submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Login'}
          </button>
        </form>

        <button
          type="button"
          className="login-link-forgot"
          onClick={() => { void handleForgotPassword(); }}
          disabled={loading}
        >
          Forgot password?
        </button>
        <button type="button" className="join-link-back" onClick={() => navigate('/')}>
          ← Back to home
        </button>
      </div>

      {debugControls}
    </div>
  );
};

export default LoginPage;
